import {
  computeInvoiceTotals,
  type InvoiceDocument,
  type InvoiceParty,
  lineItemAmount,
} from '@clockwise/shared';

/**
 * What the customer will see, rendered live from the editor's form state.
 *
 * This is an HTML mirror of `apps/api/src/core/invoices/pdf/render.ts`, not the
 * PDF itself. That is a deliberate trade with a known cost: two layouts exist,
 * and they can drift visually. It buys the thing a PDF cannot give — the pane
 * updates as the user types, with no round trip and no re-render of a document
 * for every keystroke.
 *
 * The drift is bounded by construction: this component takes an
 * {@link InvoiceDocument} — the exact shape the PDF renderer consumes — and
 * derives every figure through `computeInvoiceTotals` / `lineItemAmount`, the
 * same shared helpers the renderer and the API response use. So the *content*
 * and the *arithmetic* cannot disagree with the PDF; only the visual layout is
 * duplicated. The Download button next to this pane fetches the real PDF, which
 * remains the single source of truth for what is sent.
 *
 * The labels below ("Invoice To", "Service for the period", "Payments/Credits",
 * "Balance Due") are copied verbatim from `render.ts` so the two read
 * identically.
 */
export function InvoiceDocumentPreview({ document }: { document: InvoiceDocument }) {
  const totals = computeInvoiceTotals(document);
  const money = (v: string) => formatCurrency(v, document.currency);

  return (
    <div
      data-testid="invoice-document-preview"
      className="mx-auto w-full max-w-[850px] bg-white p-8 text-[11px] text-black shadow-sm"
    >
      {/* ---- Header: issuer left, title + Date/Invoice # right ------------ */}
      <div className="flex items-start justify-between gap-6">
        {/* `min-w-0` is load-bearing: without it this flex child refuses to
            shrink below its longest line (the email), and the address block
            slides under the Date box on the right. */}
        <div className="min-w-0 flex-1">
          {document.issuer.name ? (
            <p className="font-semibold text-[13px]">{document.issuer.name}</p>
          ) : null}
          {document.issuer.addressLines.map((line, i) => (
            <p key={`issuer-${i}-${line}`}>{line}</p>
          ))}
          <ContactRow label="Phone #" value={document.issuer.phone} extra={document.issuer.email} />
          <ContactRow label="Fax #" value={document.issuer.fax} extra={document.issuer.website} />
        </div>

        <div className="shrink-0 text-right">
          <p className="mb-2 font-semibold text-[22px] tracking-wide">Invoice</p>
          <div className="flex justify-end">
            <div className="border border-black px-3 py-0.5">Date</div>
            <div className="min-w-[92px] border border-black border-l-0 px-3 py-0.5 text-center">
              {formatUsDate(document.invoiceDate)}
            </div>
          </div>
          <p className="mt-1.5">
            <span className="mr-3">Invoice #</span>
            <span>{document.invoiceNumber}</span>
          </p>
        </div>
      </div>

      {/* ---- Invoice To ------------------------------------------------------- */}
      <div className="mt-6 w-[52%]">
        <div className="border border-black bg-neutral-200 px-2 py-1 font-medium">Invoice To</div>
        <div className="min-h-[86px] border border-black border-t-0 px-2 py-1.5">
          <PartyLines party={document.billTo} />
        </div>
      </div>

      {/* ---- Terms / Due Date --------------------------------------------- */}
      <div className="mt-5 flex items-start justify-between gap-8">
        <div className="flex">
          <div className="border border-black px-4 py-0.5">Terms</div>
          <div className="min-w-[110px] border border-black border-l-0 px-4 py-0.5 text-center">
            {document.terms ?? ''}
          </div>
        </div>
        <div className="flex">
          <div className="border border-black px-4 py-0.5">Due Date</div>
          <div className="min-w-[110px] border border-black border-l-0 px-4 py-0.5 text-center">
            {document.dueDate ? formatUsDate(document.dueDate) : ''}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-start justify-between gap-8">
        <p>
          <span className="mr-4">Service for the period</span>
          <span>{document.servicePeriod ?? ''}</span>
        </p>
      </div>

      {/* ---- Line items ---------------------------------------------------- */}
      <table className="mt-4 w-full border-collapse">
        <thead>
          <tr className="bg-neutral-200">
            <th className="border border-black px-2 py-1 text-center font-medium">Description</th>
            <th className="w-[70px] border border-black px-2 py-1 text-center font-medium">Qty</th>
            <th className="w-[90px] border border-black px-2 py-1 text-center font-medium">Rate</th>
            <th className="w-[100px] border border-black px-2 py-1 text-center font-medium">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {document.lineItems.map((line, i) => (
            <tr key={`line-${i}-${line.description}`}>
              <td className="whitespace-pre-wrap border border-black px-2 py-1 align-top">
                {line.description}
              </td>
              {/* A flat-fee row prints an empty Qty/Rate pair rather than a
                  zero — exactly as the reference invoice does. */}
              <td className="border border-black px-2 py-1 text-right align-top tabular-nums">
                {line.quantity ?? ''}
              </td>
              <td className="border border-black px-2 py-1 text-right align-top tabular-nums">
                {line.rate === undefined ? '' : formatAmount(line.rate)}
              </td>
              <td className="border border-black px-2 py-1 text-right align-top tabular-nums">
                {formatAmount(lineItemAmount(line))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ---- Remittance box + totals --------------------------------------- */}
      <div className="mt-6 flex items-start justify-between gap-8">
        <div className="min-w-[45%]">
          {document.paymentDetails.length > 0 ? (
            <div className="border border-black px-3 py-2">
              {document.paymentDetails.map((line, i) => (
                <p key={`pay-${i}-${line}`}>{line}</p>
              ))}
            </div>
          ) : null}
        </div>

        <dl className="min-w-[240px] text-right">
          <TotalRow label="Total" value={money(totals.total)} />
          <TotalRow label="Payments/Credits" value={money(totals.paymentsCredits)} />
          <TotalRow label="Balance Due" value={money(totals.balanceDue)} emphasis />
        </dl>
      </div>
    </div>
  );
}

function PartyLines({ party }: { party: InvoiceParty }) {
  return (
    <>
      {party.name ? <p>{party.name}</p> : null}
      {party.addressLines.map((line, i) => (
        <p key={`party-${i}-${line}`}>{line}</p>
      ))}
      {party.email ? <p>{party.email}</p> : null}
    </>
  );
}

/** The issuer's contact rows pair a label with a second field on the right,
 *  matching the two-column phone/email, fax/website block in the PDF. */
function ContactRow({ label, value, extra }: { label: string; value?: string; extra?: string }) {
  if (!value && !extra) return null;
  return (
    <p className="flex gap-3">
      <span className="w-12 shrink-0">{label}</span>
      <span className="w-32 shrink-0">{value ?? ''}</span>
      {/* Truncates rather than pushing the block wider — a long address would
          otherwise force the whole header past the page edge. */}
      <span className="min-w-0 truncate" title={extra}>
        {extra ?? ''}
      </span>
    </p>
  );
}

function TotalRow({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className={`flex justify-between gap-6 ${emphasis ? 'mt-1 font-bold text-[14px]' : ''}`}>
      <dt>{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}

/**
 * Money arrives as a two-decimal STRING and has to become a `number` for
 * `Intl.NumberFormat`. Safe here and nowhere else: this is the last step
 * before pixels, the value never returns to arithmetic, and anything that
 * doesn't parse is printed verbatim rather than as `NaN` — or, worse, than
 * `Number('')` rendering a missing amount as a plausible `0.00` (G15).
 *
 * Mirrors `pdf/format.ts`, which does the same at the same point.
 */
function formatDecimal(value: string, fmt: Intl.NumberFormat): string {
  if (value.trim() === '') return value;
  const n = Number(value);
  return Number.isFinite(n) ? fmt.format(n) : value;
}

const AMOUNT_FORMAT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** `'1234.5'` → `'1,234.50'`. Grouped, always two decimals, like the PDF's own
 *  amount formatter. */
function formatAmount(value: string): string {
  return formatDecimal(value, AMOUNT_FORMAT);
}

function formatCurrency(value: string, currency: string): string {
  try {
    return formatDecimal(value, new Intl.NumberFormat('en-US', { style: 'currency', currency }));
  } catch {
    // An unknown ISO code makes `Intl` throw. The figure is the useful part,
    // so print it with the raw code rather than dropping the row.
    return `${currency} ${formatAmount(value)}`;
  }
}

/** `YYYY-MM-DD` → `M/D/YYYY`, from the string's own parts. `new Date(iso)`
 *  parses a bare date as UTC midnight and renders it a day early west of
 *  Greenwich. */
function formatUsDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${Number(m)}/${Number(d)}/${y}`;
}
